import * as dayjs from 'dayjs';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { SyncEvent, SyncEventSource } from '../sync-event.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { OpenAccessPassTemplate } from './open-access-pass/open-access-pass-template.js';

export class OpenAccessPassTemplateUpdatedEvent extends SyncEvent {
  static readonly TYPE_ID: string = '730689c7ad4340039ce7caf843fbaca8';

  constructor(
      public readonly uniqueId: string,
      public readonly creatorId: string | null,
      public readonly deviceId: string | null,
      public readonly creationDate: dayjs.Dayjs,
      public readonly source: SyncEventSource,
      public readonly before: OpenAccessPassTemplate,
      public readonly after: OpenAccessPassTemplate,
  ) {
    super();
  }

  get $typeId(): string {
    return OpenAccessPassTemplateUpdatedEvent.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenAccessPassTemplateUpdatedEvent {
    return new OpenAccessPassTemplateUpdatedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        context.fromData<OpenAccessPassTemplate>(MapUtils.get<SerializedData>(map, 'before')),
        context.fromData<OpenAccessPassTemplate>(MapUtils.get<SerializedData>(map, 'after')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'beforeId': this.before.uniqueId,
      'before': context.toData(this.before),
      'afterId': this.after.uniqueId,
      'after': context.toData(this.after),
    };
  }

  clone(): this {
    return new OpenAccessPassTemplateUpdatedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.before.clone(),
        this.after.clone(),
    ) as this;
  }

  isSame(other: OpenAccessPassTemplateUpdatedEvent): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        isSameData(other.before, this.before) &&
        isSameData(other.after, this.after);
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof OpenAccessPassTemplateUpdatedEvent;
  }

  static assertType(dm: DataModel): dm is OpenAccessPassTemplateUpdatedEvent {
    return dm instanceof OpenAccessPassTemplateUpdatedEvent;
  }
}

export class OpenAccessPassTemplateUpdatedEventBuilder extends DataModelBuilder<OpenAccessPassTemplateUpdatedEvent> {
  build(context: SerializationContext, map: SerializedData): OpenAccessPassTemplateUpdatedEvent {
    return OpenAccessPassTemplateUpdatedEvent.fromMap(context, map);
  }
}
